import { Box, Button, CssBaseline, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError,
  useRouteLoaderData,
} from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { getOr, type Query } from '@/api/client';
import { carteDeError } from '@/lib/errores';
import { RUTAS } from '@/api/rutas';
import { Cabecera } from '@/components/Cabecera';
import { PieDePagina } from '@/components/PieDePagina';
import type { Imagen, Producto, Sesion } from '@/tipos';
import darkTheme from '@/theme/dark';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto-condensed/400.css';
import '@fontsource/roboto-condensed/500.css';
import '@fontsource/roboto-condensed/600.css';
import '@fontsource/roboto-condensed/700.css';
import '@/estilos.css';

/** Las cuatro piezas que necesita una marca para que su PDF exista. */
const PIEZAS_DEL_PDF = ['cover', 'back_cover', 'header', 'footer'];

export interface DatosDelSitio {
  sesion: Sesion | null;
  /** Sin catálogo activo no hay nada que descargar ni que mostrar: los botones no aparecen. */
  hayCatalogo: boolean;
  /** La sección Novedades sólo existe si hay imágenes cargadas. */
  hayNovedades: boolean;
  /** Las marcas cuyo PDF se puede generar hoy. */
  marcasConPdf: string[];
}

/**
 * Lo que la cabecera necesita saber para dibujarse bien la primera vez.
 *
 * Antes eran cinco consultas desde el navegador, después de pintar: el menú aparecía sin
 * "Novedades" y sin los PDF, y medio segundo más tarde se le agregaban los botones. Acá se
 * resuelve antes de mandar el HTML, así que llega dibujado como corresponde.
 */
export async function loader({ request }: LoaderFunctionArgs): Promise<DatosDelSitio> {
  const opciones = { request };
  const unaSola: Query = { limit: 1 };

  const [sesion, catalogo, novedades, ofertas, piezas, marcas] = await Promise.all([
    getOr<{ user: Sesion } | null>(RUTAS.sesion.quienSoy, null, opciones),
    getOr<{ products: Producto[] }>(
      RUTAS.catalogo.productos,
      { products: [] },
      { ...opciones, query: unaSola },
    ),
    getOr<{ images: Imagen[] }>(
      RUTAS.imagenes.coleccionPublica('novedades'),
      { images: [] },
      { ...opciones, query: unaSola },
    ),
    getOr<{ images: Imagen[] }>(
      RUTAS.imagenes.coleccionPublica('offers'),
      { images: [] },
      { ...opciones, query: unaSola },
    ),
    getOr<{ assets: { brand: string; asset_type: string }[] }>(
      RUTAS.piezasDelPdf.publica,
      { assets: [] },
      opciones,
    ),
    getOr<{ brands: string[] }>(RUTAS.catalogo.marcas, { brands: [] }, opciones),
  ]);

  return {
    sesion: sesion?.user ?? null,
    hayCatalogo: catalogo.products.length > 0,
    hayNovedades: novedades.images.length > 0 || ofertas.images.length > 0,
    marcasConPdf: conPdfCompleto(piezas.assets, marcas.brands),
  };
}

/**
 * Una marca ofrece PDF cuando tiene sus cuatro piezas Y productos en el catálogo activo.
 *
 * Las dos condiciones importan: sin las piezas el PDF sale con una página en blanco donde va la
 * tapa, y sin productos sale un catálogo vacío. Ofrecer el botón igual es prometer un archivo
 * roto.
 */
export function conPdfCompleto(
  piezas: { brand: string; asset_type: string }[],
  marcas: string[],
): string[] {
  const porMarca = new Map<string, Set<string>>();

  for (const pieza of piezas) {
    const tipos = porMarca.get(pieza.brand) ?? new Set<string>();
    tipos.add(pieza.asset_type);
    porMarca.set(pieza.brand, tipos);
  }

  const conProductos = new Set(marcas);

  return [...porMarca.entries()]
    .filter(([marca, tipos]) => conProductos.has(marca) && PIEZAS_DEL_PDF.every(t => tipos.has(t)))
    .map(([marca]) => marca);
}

/** Los datos del sitio, desde cualquier pantalla. */
export function useDatosDelSitio(): DatosDelSitio {
  const datos = useRouteLoaderData<DatosDelSitio>('root');

  // Sólo puede faltar dentro del ErrorBoundary, cuando el propio loader falló.
  return datos ?? { sesion: null, hayCatalogo: false, hayNovedades: false, marcasConPdf: [] };
}

/**
 * Quién es CKING, en el idioma en que lo pregunta un buscador.
 *
 * Las direcciones van sin `www`. Decían `www.cking.com.ar`, que no es donde vive el sitio: el
 * canonical de todas las páginas apunta al dominio pelado. Dos direcciones para la misma empresa
 * es pedirle a Google que elija, y elige él.
 */
const DATOS_DE_LA_EMPRESA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'CKING',
  alternateName: 'CKING MOTOS',
  url: 'https://cking.com.ar',
  logo: 'https://cking.com.ar/favicon-96x96.png',
  image: 'https://cking.com.ar/hero/hero.jpg',
  description:
    'Importadora y distribuidora mayorista de repuestos para motos en Argentina desde 2009. ' +
    'Marcas HADA, HAFEEN, VTC y artículos para Benelli.',
  slogan: 'Repuestos para motos al por mayor',
  foundingDate: '2009',
  address: { '@type': 'PostalAddress', addressCountry: 'AR' },
  areaServed: { '@type': 'Country', name: 'Argentina' },
  email: 'info@cking.com.ar',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    email: 'info@cking.com.ar',
    areaServed: 'AR',
    availableLanguage: 'Spanish',
  },
  sameAs: ['https://www.instagram.com/hada.ok/'],
};

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es" data-scroll-behavior="smooth" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
        {/* apple-icon.png y no apple-touch-icon.png: el segundo nombre estaba escrito en
            el sitio anterior y el archivo nunca existió. */}
        <link rel="apple-touch-icon" href="/apple-icon.png" />
        <link rel="manifest" href="/manifest.json" />

        {/* Donde entran los estilos de MUI. Va ANTES de <Links />: asi las reglas del tema
            quedan primero y la hoja propia del sitio puede pisarlas, que es el orden que tenia
            el `prepend` de antes. Lo llena entry.server.tsx. */}
        <meta name="emotion-insertion-point" content="" />

        {/* Lo que se ve cuando alguien pega un link del sitio en WhatsApp. Va acá y no en cada
            pantalla porque no cambia: el título y la descripción sí, y esos los pone cada una. */}
        <meta property="og:site_name" content="CKING" />
        <meta property="og:locale" content="es_AR" />
        <meta name="twitter:card" content="summary_large_image" />

        <Meta />
        <Links />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(DATOS_DE_LA_EMPRESA) }}
        />
      </head>

      <body suppressHydrationWarning>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const { pathname } = useLocation();

  // El panel dibuja su propia barra y su propio menú. La cabecera del sitio no va ahí adentro:
  // es la del visitante, con el botón de descargar el catálogo y el de iniciar sesión.
  const enElPanel = pathname === '/dashboard' || pathname.startsWith('/dashboard/');

  if (enElPanel) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Outlet />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      {/* Saltar al contenido: WCAG 2.4.1. Invisible hasta que alguien llega con el tabulador. */}
      <Box
        component="a"
        href="#contenido-principal"
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: 9999,
          '&:focus': {
            position: 'fixed',
            top: 8,
            left: 8,
            width: 'auto',
            height: 'auto',
            padding: '12px 24px',
            bgcolor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: 1,
            textDecoration: 'none',
            boxShadow: 4,
          },
        }}
      >
        Saltar al contenido principal
      </Box>

      <Cabecera />

      {/* La barra está fija; sin este hueco tapa la primera línea de cada página. */}
      <Box sx={{ height: 80 }} />

      <Box
        component="main"
        id="contenido-principal"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          color: 'text.primary',
          flexGrow: 1,
        }}
      >
        <Outlet />
      </Box>

      <PieDePagina />
    </ThemeProvider>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const ubicacion = useLocation();

  const respuesta = isRouteErrorResponse(error) ? error : null;

  // Qué decir se decide en `lib/errores`, que no necesita un router para probarse.
  const { titulo, detalle, pasajero } = carteDeError(respuesta?.status ?? null);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      <Box
        sx={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
          p: 4,
        }}
      >
        <Box sx={{ maxWidth: 640 }}>
          {/*
            El número, tratado como los códigos de pieza: condensado y con cifras tabulares. Es
            la misma tipografía que identifica una pieza en todo el sitio, y acá dice que esta
            pantalla también es parte del catálogo y no un cartel del servidor.
          */}
          <Box
            aria-hidden
            sx={{
              fontFamily: 'var(--font-roboto-condensed)',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              fontSize: 'clamp(5rem, 18vw, 11rem)',
              lineHeight: 0.85,
              letterSpacing: '-0.04em',
              color: 'primary.main',
              opacity: 0.9,
            }}
          >
            {respuesta?.status ?? 500}
          </Box>

          <Box sx={{ width: 64, height: '3px', bgcolor: 'error.main', mx: 'auto', my: 3 }} />

          <Typography variant="h2" component="h1" gutterBottom>
            {titulo}
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 4 }}>
            {detalle}
          </Typography>

          {/* Lo que se ofrece es lo que sirve en cada caso: ante un corte, volver a intentar; el
              catálogo y los rubros son para quien se perdió, no para quien está esperando que
              vuelva el servidor. */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            {pasajero ? (
              // Un enlace y no un onClick: si lo que falló fue el render, el botón tiene que
              // funcionar igual, sin que haya corrido nada del navegador.
              <Button
                component="a"
                href={`${ubicacion.pathname}${ubicacion.search}`}
                variant="contained"
                color="primary"
              >
                Reintentar
              </Button>
            ) : (
              <Button component="a" href="/catalogo" variant="contained" color="primary">
                Buscar en el catálogo
              </Button>
            )}

            {!pasajero && (
              <Button component="a" href="/rubros" variant="outlined" color="primary">
                Ver los rubros
              </Button>
            )}

            <Button component="a" href="/" variant="text" color="primary">
              Volver al inicio
            </Button>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
